import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Calendar, Download, Users, User, FileText, Clock, AlertTriangle, CheckCircle, XCircle, Filter, History, Trash2, Eye, X, Building2, Mail } from 'lucide-react';
import { toast } from 'sonner';
import { openBlobInNewTab } from '../utils/fileUtils';
import { FeuillesDuJourCard } from '../components/FeuillesDuJourCard';

const PresenceCentre = ({ user }) => {
  const [stagiaires, setStagiaires] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);
  const [presencesCollectives, setPresencesCollectives] = useState([]);
  const [selectedStagiaire, setSelectedStagiaire] = useState('');
  const [presencesIndividuelles, setPresencesIndividuelles] = useState([]);
  const [stats, setStats] = useState({ present: 0, absent: 0, retard: 0, justifie: 0, presentCentre: 0, presentEntreprise: 0, absentCentre: 0, absentEntreprise: 0, retardCentre: 0, retardEntreprise: 0 });
  const [loading, setLoading] = useState(true);
  
  // Filtres de période pour l'export
  const [dateDebut, setDateDebut] = useState('');
  const [dateFin, setDateFin] = useState('');
  const [selectedCentre, setSelectedCentre] = useState('all');
  
  // Historique des feuilles générées
  const [historiqueFeuilies, setHistoriqueFeuilles] = useState([]);
  const [filtreHistorique, setFiltreHistorique] = useState('all');
  const [filtrePeriode, setFiltrePeriode] = useState('all');
  
  // Aperçu PDF
  const [showPdfPreview, setShowPdfPreview] = useState(false);
  const [pdfPreviewUrl, setPdfPreviewUrl] = useState('');
  const [sendingRecap, setSendingRecap] = useState(false);
  
  // Modale détails présences
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [detailModalData, setDetailModalData] = useState({ type: '', titre: '', stagiaires: [] });

  useEffect(() => {
    loadStagiaires();
    loadHistoriqueFeuilles();
  }, []);

  useEffect(() => {
    loadPresencesCollectives();
  }, [selectedDate]);

  useEffect(() => {
    if (selectedStagiaire) {
      loadPresencesIndividuelles();
    }
  }, [selectedStagiaire]);

  useEffect(() => {
    loadHistoriqueFeuilles();
  }, [filtreHistorique, filtrePeriode]);

  const loadStagiaires = async () => {
    try {
      const response = await axios.get('/stagiaires');
      const stagiairesList = response.data.filter(s => s.statut_validation !== 'supprime');
      setStagiaires(stagiairesList);
      if (stagiairesList.length > 0) {
        setSelectedStagiaire(stagiairesList[0].user_id);
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const loadPresencesCollectives = async () => {
    try {
      const response = await axios.get(`/presences/date/${selectedDate}`);
      setPresencesCollectives(response.data);
      
      // Calculer les stats
      const statuses = response.data.reduce((acc, p) => {
        acc[p.statut] = (acc[p.statut] || 0) + 1;
        // Distinction Centre / Entreprise
        const source = p.type_presence === 'entreprise' ? 'Entreprise' : 'Centre';
        acc[`${p.statut}${source}`] = (acc[`${p.statut}${source}`] || 0) + 1;
        return acc;
      }, {});
      setStats({
        present: statuses.present || 0,
        absent: statuses.absent || 0,
        retard: statuses.retard || 0,
        justifie: statuses.justifie || 0,
        presentCentre: statuses.presentCentre || 0,
        presentEntreprise: statuses.presentEntreprise || 0,
        absentCentre: statuses.absentCentre || 0,
        absentEntreprise: statuses.absentEntreprise || 0,
        retardCentre: statuses.retardCentre || 0,
        retardEntreprise: statuses.retardEntreprise || 0,
        justifieCentre: statuses.justifieCentre || 0,
        justifieEntreprise: statuses.justifieEntreprise || 0
      });
    } catch (error) {
    }
  };

  const loadPresencesIndividuelles = async () => {
    try {
      const response = await axios.get(`/presences/stagiaire/${selectedStagiaire}`);
      setPresencesIndividuelles(response.data);
    } catch (error) {
    }
  };

  const loadHistoriqueFeuilles = async () => {
    try {
      const response = await axios.get('/feuilles-presence/historique', {
        params: { type_filtre: filtreHistorique, periode: filtrePeriode }
      });
      setHistoriqueFeuilles(response.data);
    } catch (error) {
    }
  };

  // Ouvrir la modale de détails avec les noms des stagiaires
  const openDetailModal = (statut, titre) => {
    const filtered = presencesCollectives.filter(p => p.statut === statut);
    const stagiairesList = filtered.map(p => {
      const stag = stagiaires.find(s => s.user_id === p.stagiaire_id);
      return {
        nom: stag ? `${stag.user_info?.prenom || ''} ${stag.user_info?.nom || ''}` : 'Inconnu',
        centre: stag?.centre_formation || '',
        source: p.type_presence === 'entreprise' ? 'Entreprise' : 'Centre',
        periode: p.periode === 'matin' ? 'Matin' : 'Après-midi'
      };
    });
    setDetailModalData({ type: statut, titre, stagiaires: stagiairesList });
    setShowDetailModal(true);
  };

  const enregistrerFeuilleDansHistorique = async (type_feuille, stagiaire_id, stagiaire_nom, date_debut, date_fin, centre, format_export) => {
    try {
      await axios.post('/feuilles-presence/historique', null, {
        params: {
          type_feuille,
          stagiaire_id,
          stagiaire_nom,
          date_debut,
          date_fin,
          centre,
          format_export
        }
      });
      loadHistoriqueFeuilles();
    } catch (error) {
    }
  };

  const supprimerFeuille = async (feuille_id) => {
    if (!window.confirm('Supprimer cette entrée de l\'historique ?')) return;
    try {
      await axios.delete(`/feuilles-presence/historique/${feuille_id}`);
      toast.success('Entrée supprimée');
      loadHistoriqueFeuilles();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const downloadPresenceCollective = async () => {
    try {
      // Construire l'URL avec la date de fin si spécifiée
      let url = `/pdf/presence-collective/${selectedDate}`;
      if (dateFin) {
        url += `?date_fin=${dateFin}`;
      }
      const response = await axios.get(url, { responseType: 'blob' });
      const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = blobUrl;
      const filename = dateFin 
        ? `presence_collective_${selectedDate}_au_${dateFin}.pdf`
        : `presence_collective_${selectedDate}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Feuille collective téléchargée');
      // Enregistrer dans l'historique
      await enregistrerFeuilleDansHistorique('collective_centre', null, null, selectedDate, dateFin, null, 'pdf');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const previewPresenceCollective = async () => {
    try {
      let url = `/pdf/presence-collective/${selectedDate}`;
      if (dateFin) {
        url += `?date_fin=${dateFin}`;
      }
      toast.info('Génération du PDF...');
      const response = await axios.get(url, { responseType: 'blob' });
      openBlobInNewTab(new Blob([response.data], { type: 'application/pdf' }));
      toast.success('PDF ouvert dans un nouvel onglet');
    } catch (error) {
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const previewPresenceIndividuelle = async () => {
    if (!selectedStagiaire) {
      toast.error('Veuillez sélectionner un stagiaire');
      return;
    }
    try {
      toast.info('Génération du PDF...');
      const response = await axios.get(`/pdf/feuille-presence/${selectedStagiaire}`, { responseType: 'blob' });
      openBlobInNewTab(new Blob([response.data], { type: 'application/pdf' }));
      toast.success('PDF ouvert dans un nouvel onglet');
    } catch (error) {
      toast.error('Erreur lors de la génération du PDF');
    }
  };

  const downloadPresenceIndividuelle = async () => {
    if (!selectedStagiaire) return;
    try {
      const response = await axios.get(`/pdf/feuille-presence/${selectedStagiaire}`, { responseType: 'blob' });
      const stag = stagiaires.find(s => s.user_id === selectedStagiaire);
      const stagNom = `${stag?.user_info?.prenom || ''} ${stag?.user_info?.nom || ''}`.trim();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${stag?.user_info?.nom || 'stagiaire'}_${stag?.user_info?.prenom || ''}_presence.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Feuille individuelle téléchargée');
      // Enregistrer dans l'historique
      await enregistrerFeuilleDansHistorique('individuelle_centre', selectedStagiaire, stagNom, new Date().toISOString().split('T')[0], null, stag?.centre_formation, 'pdf');
    } catch (error) {
      toast.error('Erreur lors du téléchargement');
    }
  };

  const downloadPresenceCollectiveExcel = async () => {
    try {
      const response = await axios.get(`/excel/presence-collective/${selectedDate}`, { responseType: 'blob' });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${selectedDate}_presence_collective.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Feuille collective Excel téléchargée');
      // Enregistrer dans l'historique
      await enregistrerFeuilleDansHistorique('collective_centre', null, null, selectedDate, null, null, 'excel');
    } catch (error) {
      toast.error('Erreur lors du téléchargement Excel');
    }
  };

  const handleEnvoyerRecapDirection = async () => {
    setSendingRecap(true);
    try {
      const res = await axios.post('/presences/envoyer-recap-direction', { date: selectedDate });
      toast.success(res.data.message || 'Récapitulatif envoyé à la direction');
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erreur lors de l'envoi du récapitulatif");
    } finally {
      setSendingRecap(false);
    }
  };

  const downloadPresenceIndividuelleExcel = async () => {
    if (!selectedStagiaire) return;
    try {
      const response = await axios.get(`/excel/feuille-presence/${selectedStagiaire}`, { responseType: 'blob' });
      const stag = stagiaires.find(s => s.user_id === selectedStagiaire);
      const stagNom = `${stag?.user_info?.prenom || ''} ${stag?.user_info?.nom || ''}`.trim();
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `${stag?.user_info?.nom || 'stagiaire'}_${stag?.user_info?.prenom || ''}_presence.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Feuille individuelle Excel téléchargée');
      // Enregistrer dans l'historique
      await enregistrerFeuilleDansHistorique('individuelle_centre', selectedStagiaire, stagNom, new Date().toISOString().split('T')[0], null, stag?.centre_formation, 'excel');
    } catch (error) {
      toast.error('Erreur lors du téléchargement Excel');
    }
  };

  const getStatusBadge = (statut) => {
    switch (statut) {
      case 'present':
        return <span className="px-2 py-1 bg-green-100 text-green-700 text-xs rounded-full">Présent</span>;
      case 'absent':
        return <span className="px-2 py-1 bg-red-100 text-red-700 text-xs rounded-full">Absent</span>;
      case 'retard':
        return <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">Retard</span>;
      case 'justifie':
        return <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">Absence justifiée</span>;
      default:
        return <span className="px-2 py-1 bg-slate-100 text-slate-700 text-xs rounded-full">—</span>;
    }
  };

  const currentStagiaire = stagiaires.find(s => s.user_id === selectedStagiaire);

  // Stats individuelles
  const statsIndiv = presencesIndividuelles.reduce((acc, p) => {
    acc[p.statut] = (acc[p.statut] || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return <div className="text-center py-8">Chargement...</div>;
  }

  return (
    <div className="space-y-6 overflow-x-hidden" data-testid="presence-centre">
      <div>
        <h1 className="text-2xl sm:text-4xl font-display font-bold text-[#0F172A] mb-2">Feuilles de Présence</h1>
        <p className="text-sm sm:text-base text-slate-600">Consultez et téléchargez les feuilles de présence</p>
      </div>

      {/* Feuille Collective Multi-jours */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 sm:p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
          <h2 className="text-base sm:text-xl font-display font-semibold text-[#0F172A] flex items-center gap-2">
            <Users className="h-4 w-4 sm:h-5 sm:w-5 text-[#8DC63F] flex-shrink-0" />
            Feuille Collective
          </h2>
          <div className="flex flex-wrap gap-2">
            <Button onClick={previewPresenceCollective} variant="outline" size="sm" data-testid="preview-collective-pdf">
              <Eye className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Aperçu</span>
            </Button>
            <Button onClick={downloadPresenceCollective} size="sm" className="bg-[#8DC63F] hover:bg-[#7db336]">
              <Download className="h-4 w-4 sm:mr-1" />
              PDF
            </Button>
            <Button onClick={downloadPresenceCollectiveExcel} variant="outline" size="sm" className="border-green-500 text-green-600 hover:bg-green-50">
              <FileText className="h-4 w-4 sm:mr-1" />
              Excel
            </Button>
            <Button onClick={handleEnvoyerRecapDirection} variant="outline" size="sm" disabled={sendingRecap} className="border-blue-400 text-blue-600 hover:bg-blue-50" data-testid="send-recap-direction-centre-button">
              <Mail className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">{sendingRecap ? 'Envoi...' : 'Récap'}</span>
            </Button>
          </div>
        </div>

        {/* Sélection période */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div>
            <Label htmlFor="date">Date de début</Label>
            <input
              type="date"
              id="date"
              value={selectedDate}
              onChange={(e) => setSelectedDate(e.target.value)}
              className="mt-1 block w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
            />
          </div>
          <div>
            <Label htmlFor="dateFin">Date de fin (optionnel)</Label>
            <input
              type="date"
              id="dateFin"
              value={dateFin}
              onChange={(e) => setDateFin(e.target.value)}
              min={selectedDate}
              className="mt-1 block w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
            />
          </div>
          <div className="flex items-end pb-2">
            <p className="text-xs text-slate-500">
              {dateFin 
                ? `📅 Période: ${new Date(selectedDate).toLocaleDateString('fr-FR')} au ${new Date(dateFin).toLocaleDateString('fr-FR')}` 
                : '💡 Laissez vide pour une feuille sur 5 jours à partir de la date de début'}
            </p>
          </div>
        </div>

        {/* Stats du jour avec distinction Centre/Entreprise */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div 
            className="p-4 bg-green-50 border border-green-200 rounded-lg text-center cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => openDetailModal('present', 'Présents')}
          >
            <CheckCircle className="h-6 w-6 text-green-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-green-700">{stats.present}</p>
            <p className="text-xs text-green-600">Présents</p>
            <div className="mt-2 flex justify-center gap-2 text-[10px]">
              <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">Centre: {stats.presentCentre}</span>
              <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded">Ent: {stats.presentEntreprise}</span>
            </div>
          </div>
          <div 
            className="p-4 bg-red-50 border border-red-200 rounded-lg text-center cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => openDetailModal('absent', 'Absents')}
          >
            <XCircle className="h-6 w-6 text-red-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-red-700">{stats.absent}</p>
            <p className="text-xs text-red-600">Absents</p>
            <div className="mt-2 flex justify-center gap-2 text-[10px]">
              <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">Centre: {stats.absentCentre}</span>
              <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded">Ent: {stats.absentEntreprise}</span>
            </div>
          </div>
          <div 
            className="p-4 bg-orange-50 border border-orange-200 rounded-lg text-center cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => openDetailModal('retard', 'Retards')}
          >
            <Clock className="h-6 w-6 text-orange-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-orange-700">{stats.retard}</p>
            <p className="text-xs text-orange-600">Retards</p>
            <div className="mt-2 flex justify-center gap-2 text-[10px]">
              <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">Centre: {stats.retardCentre}</span>
              <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded">Ent: {stats.retardEntreprise}</span>
            </div>
          </div>
          <div 
            className="p-4 bg-blue-50 border border-blue-200 rounded-lg text-center cursor-pointer hover:shadow-md transition-shadow"
            onClick={() => openDetailModal('justifie', 'Justifiés')}
          >
            <FileText className="h-6 w-6 text-blue-600 mx-auto mb-2" />
            <p className="text-2xl font-bold text-blue-700">{stats.justifie}</p>
            <p className="text-xs text-blue-600">Justifiés</p>
            <div className="mt-2 flex justify-center gap-2 text-[10px]">
              <span className="px-1.5 py-0.5 bg-blue-100 text-blue-700 rounded">Centre: {stats.justifieCentre || 0}</span>
              <span className="px-1.5 py-0.5 bg-orange-100 text-orange-700 rounded">Ent: {stats.justifieEntreprise || 0}</span>
            </div>
          </div>
        </div>

        {/* Tableau présences du jour */}
        {presencesCollectives.length === 0 ? (
          <p className="text-slate-500 text-center py-8">Aucune présence enregistrée pour cette date</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Stagiaire</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Centre</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Matin</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Après-midi</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Signature</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {stagiaires.map((stag) => {
                  const matinPresence = presencesCollectives.find(p => p.stagiaire_id === stag.user_id && p.periode === 'matin');
                  const apmPresence = presencesCollectives.find(p => p.stagiaire_id === stag.user_id && p.periode === 'apres_midi');
                  return (
                    <tr key={stag.user_id} className="hover:bg-slate-50">
                      <td className="px-4 py-3 font-medium">
                        {stag.user_info?.prenom} {stag.user_info?.nom}
                      </td>
                      <td className="px-4 py-3 text-slate-600">{stag.centre_formation}</td>
                      <td className="px-4 py-3">{matinPresence ? getStatusBadge(matinPresence.statut) : '—'}</td>
                      <td className="px-4 py-3">{apmPresence ? getStatusBadge(apmPresence.statut) : '—'}</td>
                      <td className="px-4 py-3">
                        {(matinPresence?.signature_stagiaire || apmPresence?.signature_stagiaire) ? (
                          <CheckCircle className="h-4 w-4 text-green-500" />
                        ) : (
                          <span className="text-slate-400">—</span>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Feuille Individuelle par Stagiaire */}
      <div className="bg-white border border-slate-200 rounded-lg p-3 sm:p-6 shadow-sm">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-4 sm:mb-6">
          <h2 className="text-base sm:text-xl font-display font-semibold text-[#0F172A] flex items-center gap-2">
            <User className="h-4 w-4 sm:h-5 sm:w-5 text-[#8DC63F] flex-shrink-0" />
            Feuille Individuelle
          </h2>
          <div className="flex flex-wrap gap-2">
            <Button onClick={previewPresenceIndividuelle} variant="outline" size="sm">
              <Eye className="h-4 w-4 sm:mr-1" />
              <span className="hidden sm:inline">Aperçu</span>
            </Button>
            <Button onClick={downloadPresenceIndividuelle} variant="outline" size="sm">
              <Download className="h-4 w-4 sm:mr-1" />
              PDF
            </Button>
            <Button onClick={downloadPresenceIndividuelleExcel} variant="outline" size="sm" className="border-green-500 text-green-600 hover:bg-green-50">
              <FileText className="h-4 w-4 sm:mr-1" />
              Excel
            </Button>
          </div>
        </div>

        {/* Sélection stagiaire */}
        <div className="mb-6">
          <Label htmlFor="stagiaire">Stagiaire</Label>
          <select
            id="stagiaire"
            value={selectedStagiaire}
            onChange={(e) => setSelectedStagiaire(e.target.value)}
            className="mt-1 block w-full md:w-96 px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
          >
            {stagiaires.map((s) => (
              <option key={s.user_id} value={s.user_id}>
                {s.user_info?.prenom} {s.user_info?.nom} - {s.centre_formation}
              </option>
            ))}
          </select>
        </div>

        {currentStagiaire && (
          <div className="mb-6 p-4 bg-slate-50 rounded-lg">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 bg-[#8DC63F]/10 rounded-full flex items-center justify-center">
                  <User className="h-6 w-6 text-[#8DC63F]" />
                </div>
                <div>
                  <p className="font-semibold text-[#0F172A]">
                    {currentStagiaire.user_info?.prenom} {currentStagiaire.user_info?.nom}
                  </p>
                  <p className="text-sm text-slate-600">
                    {currentStagiaire.centre_formation} - {currentStagiaire.statut}
                  </p>
                </div>
              </div>
              <div className="flex gap-4 text-center">
                <div>
                  <p className="text-xl font-bold text-green-600">{statsIndiv.present || 0}</p>
                  <p className="text-xs text-slate-600">Présent</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-red-600">{statsIndiv.absent || 0}</p>
                  <p className="text-xs text-slate-600">Absent</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-orange-600">{statsIndiv.retard || 0}</p>
                  <p className="text-xs text-slate-600">Retard</p>
                </div>
                <div>
                  <p className="text-xl font-bold text-blue-600">{statsIndiv.justifie || 0}</p>
                  <p className="text-xs text-slate-600">Justifié</p>
                </div>
              </div>
            </div>

            {/* Alertes absences/retards */}
            {((statsIndiv.absent || 0) > 2 || (statsIndiv.retard || 0) > 3) && (
              <div className="mt-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-red-600" />
                <span className="text-sm text-red-700">
                  {(statsIndiv.absent || 0) > 2 && `${statsIndiv.absent} absences détectées. `}
                  {(statsIndiv.retard || 0) > 3 && `${statsIndiv.retard} retards détectés.`}
                </span>
              </div>
            )}
          </div>
        )}

        {/* Historique présences individuelles */}
        {presencesIndividuelles.length === 0 ? (
          <p className="text-slate-500 text-center py-8">Aucune présence enregistrée pour ce stagiaire</p>
        ) : (
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Date</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Période</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Statut</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Motif</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Signature</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {presencesIndividuelles.sort((a, b) => new Date(b.date) - new Date(a.date)).map((presence, idx) => (
                  <tr key={idx} className="hover:bg-slate-50">
                    <td className="px-4 py-3">{new Date(presence.date).toLocaleDateString('fr-FR')}</td>
                    <td className="px-4 py-3">{presence.periode === 'matin' ? 'Matin' : 'Après-midi'}</td>
                    <td className="px-4 py-3">{getStatusBadge(presence.statut)}</td>
                    <td className="px-4 py-3 text-slate-600">{presence.motif || '—'}</td>
                    <td className="px-4 py-3">
                      {presence.signature_stagiaire ? (
                        <CheckCircle className="h-4 w-4 text-green-500" />
                      ) : (
                        <span className="text-slate-400">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Export par Période */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-center gap-2 mb-4">
          <Filter className="h-5 w-5 text-[#8DC63F]" />
          <h2 className="text-xl font-display font-semibold text-[#0F172A]">Export par Période</h2>
        </div>
        <p className="text-sm text-slate-600 mb-4">Sélectionnez une période pour exporter les feuilles de présence</p>
        
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
          <div className="flex flex-col space-y-1">
            <Label className="text-xs flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Date de début
            </Label>
            <input
              type="date"
              value={dateDebut}
              onChange={(e) => setDateDebut(e.target.value)}
              className="h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              data-testid="date-debut-export"
            />
          </div>
          <div className="flex flex-col space-y-1">
            <Label className="text-xs flex items-center gap-1">
              <Calendar className="h-3 w-3" /> Date de fin
            </Label>
            <input
              type="date"
              value={dateFin}
              onChange={(e) => setDateFin(e.target.value)}
              className="h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              data-testid="date-fin-export"
            />
          </div>
          <div className="flex flex-col space-y-1">
            <Label className="text-xs">Centre de formation</Label>
            <select
              value={selectedCentre}
              onChange={(e) => setSelectedCentre(e.target.value)}
              className="h-10 px-3 py-2 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              data-testid="select-centre-export"
            >
              <option value="all">Tous les centres</option>
              <option value="Atlantique">Atlantique</option>
              <option value="Occitanie">Occitanie</option>
            </select>
          </div>
          <div className="flex flex-col space-y-1">
            <Label className="text-xs">Type d'export</Label>
            <div className="flex gap-2">
              <Button 
                onClick={async () => {
                  if (!dateDebut || !dateFin) {
                    toast.error('Veuillez sélectionner une période');
                    return;
                  }
                  try {
                    toast.info('Export PDF en cours...');
                    const response = await axios.get(`/export/presences-periode`, {
                      params: { date_debut: dateDebut, date_fin: dateFin, centre: selectedCentre, format: 'pdf' },
                      responseType: 'blob'
                    });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `${dateDebut}_${dateFin}_presence_collective.pdf`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    toast.success('PDF téléchargé !');
                  } catch (error) {
                    toast.error('Erreur lors de l\'export PDF');
                  }
                }}
                className="flex-1 bg-[#8DC63F] hover:bg-[#7db336]"
                disabled={!dateDebut || !dateFin}
              >
                <Download className="h-4 w-4 mr-1" />
                PDF
              </Button>
              <Button 
                onClick={async () => {
                  if (!dateDebut || !dateFin) {
                    toast.error('Veuillez sélectionner une période');
                    return;
                  }
                  try {
                    toast.info('Export Excel en cours...');
                    const response = await axios.get(`/export/presences-periode`, {
                      params: { date_debut: dateDebut, date_fin: dateFin, centre: selectedCentre, format: 'excel' },
                      responseType: 'blob'
                    });
                    const url = window.URL.createObjectURL(new Blob([response.data]));
                    const link = document.createElement('a');
                    link.href = url;
                    link.setAttribute('download', `presences_${dateDebut}_${dateFin}.xlsx`);
                    document.body.appendChild(link);
                    link.click();
                    link.remove();
                    toast.success('Excel téléchargé !');
                  } catch (error) {
                    toast.error('Erreur lors de l\'export Excel');
                  }
                }}
                variant="outline"
                className="flex-1 border-green-500 text-green-600"
                disabled={!dateDebut || !dateFin}
              >
                <FileText className="h-4 w-4 mr-1" />
                Excel
              </Button>
            </div>
          </div>
        </div>
        
        {dateDebut && dateFin && (
          <p className="text-xs text-slate-500">
            Période sélectionnée : du {new Date(dateDebut).toLocaleDateString('fr-FR')} au {new Date(dateFin).toLocaleDateString('fr-FR')}
            {selectedCentre !== 'all' && ` - Centre: ${selectedCentre}`}
          </p>
        )}
      </div>

      {/* Feuilles du jour conservées */}
      <FeuillesDuJourCard />

      {/* Historique des Feuilles Générées */}
      <div className="bg-white border border-slate-200 rounded-lg p-6 shadow-sm">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-xl font-display font-semibold text-[#0F172A] flex items-center gap-2">
            <History className="h-5 w-5 text-[#8DC63F]" />
            Historique des Feuilles Générées
          </h2>
        </div>
        
        {/* Filtres historique */}
        <div className="flex flex-wrap gap-4 mb-4">
          <div className="flex flex-col space-y-1">
            <Label className="text-xs">Type de feuille</Label>
            <select
              value={filtreHistorique}
              onChange={(e) => setFiltreHistorique(e.target.value)}
              className="h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              data-testid="filtre-type-historique"
            >
              <option value="all">Tous les types</option>
              <option value="individuelle_centre">Individuelle (Centre)</option>
              <option value="collective_centre">Collective (Centre)</option>
              <option value="individuelle_entreprise">Individuelle (Entreprise)</option>
            </select>
          </div>
          <div className="flex flex-col space-y-1">
            <Label className="text-xs">Période</Label>
            <select
              value={filtrePeriode}
              onChange={(e) => setFiltrePeriode(e.target.value)}
              className="h-9 px-3 text-sm border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-[#8DC63F]"
              data-testid="filtre-periode-historique"
            >
              <option value="all">Toutes les périodes</option>
              <option value="jour">Dernières 24h</option>
              <option value="semaine">Cette semaine</option>
              <option value="mois">Ce mois</option>
            </select>
          </div>
        </div>
        
        {/* Liste historique */}
        {historiqueFeuilies.length === 0 ? (
          <div className="text-center py-8">
            <History className="h-12 w-12 text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500">Aucune feuille générée</p>
            <p className="text-sm text-slate-400">Les feuilles téléchargées apparaîtront ici</p>
          </div>
        ) : (
          <div className="overflow-x-auto max-h-96 overflow-y-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Date génération</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Type</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Stagiaire</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Période</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Format</th>
                  <th className="px-4 py-3 text-left font-semibold text-slate-700">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-200">
                {historiqueFeuilies.map((feuille) => (
                  <tr key={feuille.feuille_id} className="hover:bg-slate-50">
                    <td className="px-4 py-3">
                      {new Date(feuille.date_generation).toLocaleDateString('fr-FR', { 
                        day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' 
                      })}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        feuille.type_feuille === 'collective_centre' 
                          ? 'bg-blue-100 text-blue-700' 
                          : feuille.type_feuille === 'individuelle_entreprise'
                          ? 'bg-orange-100 text-orange-700'
                          : 'bg-green-100 text-green-700'
                      }`}>
                        {feuille.type_feuille === 'collective_centre' && 'Collective'}
                        {feuille.type_feuille === 'individuelle_centre' && 'Individuelle'}
                        {feuille.type_feuille === 'individuelle_entreprise' && 'Entreprise'}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {feuille.stagiaire_nom || '—'}
                    </td>
                    <td className="px-4 py-3 text-slate-600">
                      {feuille.date_debut ? new Date(feuille.date_debut).toLocaleDateString('fr-FR') : '—'}
                      {feuille.date_fin && ` - ${new Date(feuille.date_fin).toLocaleDateString('fr-FR')}`}
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded ${
                        feuille.format_export === 'pdf' ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700'
                      }`}>
                        {feuille.format_export?.toUpperCase()}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <div className="flex gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={async () => {
                            // Re-télécharger le fichier avec axios et blob
                            try {
                              let url = '';
                              let filename = '';
                              const format = feuille.format_export || 'pdf';
                              
                              if (feuille.type_feuille === 'individuelle_centre' && feuille.stagiaire_id) {
                                url = `/${format}/feuille-presence/${feuille.stagiaire_id}`;
                                filename = `presence_${feuille.stagiaire_nom || 'stagiaire'}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
                              } else if (feuille.type_feuille === 'collective_centre' && feuille.date_debut) {
                                url = `/${format}/presence-collective/${feuille.date_debut.split('T')[0]}`;
                                filename = `presence_collective_${feuille.date_debut.split('T')[0]}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
                              } else if (feuille.type_feuille === 'individuelle_entreprise' && feuille.stagiaire_id) {
                                url = `/${format}/feuille-presence-entreprise/${feuille.stagiaire_id}`;
                                filename = `presence_entreprise_${feuille.stagiaire_nom || 'stagiaire'}.${format === 'pdf' ? 'pdf' : 'xlsx'}`;
                              }
                              
                              if (url) {
                                toast.info('Téléchargement en cours...');
                                const response = await axios.get(url, { responseType: 'blob' });
                                const blobUrl = window.URL.createObjectURL(new Blob([response.data]));
                                const link = document.createElement('a');
                                link.href = blobUrl;
                                link.setAttribute('download', filename);
                                document.body.appendChild(link);
                                link.click();
                                link.remove();
                                toast.success('Téléchargement terminé');
                              } else {
                                toast.error('Impossible de télécharger cette feuille');
                              }
                            } catch (error) {
                              toast.error('Erreur lors du téléchargement');
                            }
                          }}
                          className="text-[#8DC63F] hover:text-[#7db336] hover:bg-green-50"
                          title="Télécharger"
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm" 
                          onClick={() => supprimerFeuille(feuille.feuille_id)}
                          className="text-red-500 hover:text-red-700 hover:bg-red-50"
                          title="Supprimer"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Modal Aperçu PDF */}
      {showPdfPreview && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg shadow-2xl w-full max-w-6xl h-[90vh] flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-slate-200">
              <h3 className="text-lg font-semibold text-[#0F172A]">Aperçu de la feuille de présence</h3>
              <Button 
                variant="ghost" 
                size="sm" 
                onClick={() => {
                  setShowPdfPreview(false);
                  if (pdfPreviewUrl) window.URL.revokeObjectURL(pdfPreviewUrl);
                  setPdfPreviewUrl('');
                }}
                className="text-slate-500 hover:text-slate-700"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
            <div className="flex-1 p-2 bg-slate-100 overflow-auto">
              <object
                data={pdfPreviewUrl}
                type="application/pdf"
                className="w-full h-full rounded border border-slate-300"
              >
                <p className="text-center p-4 text-slate-500">
                  Votre navigateur ne supporte pas l'affichage PDF intégré.{' '}
                  <a href={pdfPreviewUrl} target="_blank" rel="noopener noreferrer" className="text-[#8DC63F] underline">
                    Cliquez ici pour télécharger
                  </a>
                </p>
              </object>
            </div>
            <div className="p-4 border-t border-slate-200 flex justify-end gap-2">
              <Button 
                variant="outline"
                onClick={() => {
                  setShowPdfPreview(false);
                  if (pdfPreviewUrl) window.URL.revokeObjectURL(pdfPreviewUrl);
                  setPdfPreviewUrl('');
                }}
              >
                Fermer
              </Button>
              <Button 
                className="bg-[#8DC63F] hover:bg-[#7db336] text-white"
                onClick={() => {
                  const link = document.createElement('a');
                  link.href = pdfPreviewUrl;
                  link.download = `${new Date().getFullYear()}_feuille_presence_collective.pdf`;
                  link.click();
                }}
              >
                <Download className="h-4 w-4 mr-2" />
                Télécharger
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Modale détails présences */}
      {showDetailModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setShowDetailModal(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-md mx-4" onClick={(e) => e.stopPropagation()}>
            <div className={`p-4 rounded-t-xl flex items-center justify-between ${
              detailModalData.type === 'present' ? 'bg-green-500' :
              detailModalData.type === 'absent' ? 'bg-red-500' :
              detailModalData.type === 'retard' ? 'bg-orange-500' :
              'bg-blue-500'
            } text-white`}>
              <h3 className="font-semibold text-lg flex items-center gap-2">
                {detailModalData.type === 'present' && <CheckCircle className="h-5 w-5" />}
                {detailModalData.type === 'absent' && <XCircle className="h-5 w-5" />}
                {detailModalData.type === 'retard' && <Clock className="h-5 w-5" />}
                {detailModalData.type === 'justifie' && <FileText className="h-5 w-5" />}
                {detailModalData.titre} du jour
              </h3>
              <button onClick={() => setShowDetailModal(false)} className="hover:bg-white/20 p-1 rounded">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 max-h-80 overflow-y-auto">
              {detailModalData.stagiaires.length === 0 ? (
                <p className="text-slate-500 text-center py-4">Aucun stagiaire dans cette catégorie</p>
              ) : (
                <div className="space-y-2">
                  {detailModalData.stagiaires.map((s, idx) => (
                    <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                      <div>
                        <p className="font-medium text-slate-800">{s.nom}</p>
                        <p className="text-xs text-slate-500">{s.centre} • {s.periode}</p>
                      </div>
                      <span className={`px-2 py-1 text-xs rounded-full ${
                        s.source === 'Centre' ? 'bg-blue-100 text-blue-700' : 'bg-orange-100 text-orange-700'
                      }`}>
                        {s.source === 'Centre' ? '🏫 Centre' : '🏢 Entreprise'}
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className="p-4 border-t border-slate-200 text-center">
              <p className="text-xs text-slate-500">
                Total: {detailModalData.stagiaires.length} stagiaire(s)
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PresenceCentre;
